/**
 * Splits a heading into one span per character, so a stylesheet can stagger
 * their arrival — the signature of the reference animation for the Nata
 * chapter (dribbble.com/shots/9073748), where the type lands letter by letter
 * rather than as a block.
 *
 *   <h2 data-split-letters>La nata, kezako ?</h2>
 *
 * Each span carries --i, its index, and the heading carries --n, the total.
 * With those two the stylesheet can turn any progress from 0 to 1 into "how
 * far along the word are we" without a line of JavaScript per letter — which
 * is what lets the cascade be tied to the scroll rather than to a trigger.
 *
 * Done in JavaScript and not in Liquid for one reason: Liquid's `split` works
 * on bytes-ish strings, and would cut an accented character in half. Array.from
 * iterates real code points, so "é" stays one letter.
 *
 * Accessibility: the heading keeps its full text as an aria-label and the
 * spans are hidden from the accessibility tree — a screen reader announces the
 * sentence, not a column of individual letters. Without JavaScript the heading
 * is simply the plain text it was rendered as.
 *
 * window.yumbeiSplitLetters(element) re-cuts one heading on demand. The
 * product showcase needs it: switching flavour rewrites those headings through
 * textContent, which wipes the spans, and they have to be rebuilt on the new
 * words before the stylesheet can stagger them again.
 *
 *   <h2 data-split-letters="words">Viens nous parler, on ne mords pas !</h2>
 *
 * The "words" variant additionally wraps each word in a <span class="word">,
 * and it exists because of what the letters cost: a stylesheet has to make them
 * `display: inline-block` for them to move at all (transforms do not apply to
 * an inline box), and an atomic inline is a line-breaking opportunity. Every
 * letter therefore becomes somewhere the line may break, and a narrow column
 * starts cutting words in half — "Viens nous p / arler, on ne", measured at
 * 375px. Give the stylesheet `.word { display: inline-block; white-space:
 * nowrap }` and the word becomes the atom instead: it never breaks inside, and
 * the plain spaces left between words are the only places a line can turn.
 *
 * Opt-in rather than the default only to keep the blast radius at nothing: the
 * two headings already using this script pass the bare attribute and are built
 * exactly as before. --i still counts every character, spaces included, so the
 * cascade keeps the same rhythm in both modes.
 */
(function () {
  function split(heading, force) {
    if (heading.dataset.splitDone && !force) return;
    heading.dataset.splitDone = '1';

    var text = heading.textContent.trim();
    if (!text) return;

    heading.setAttribute('aria-label', text);

    var holder = document.createElement('span');
    holder.setAttribute('aria-hidden', 'true');
    var byWord = heading.dataset.splitLetters === 'words';
    var word = null;

    var letters = Array.from(text);
    heading.style.setProperty('--n', letters.length);
    for (var i = 0; i < letters.length; i++) {
      // In word mode the gaps are real text nodes: they are the only soft wrap
      // opportunities left, so they must not be spans, and must not be nbsp.
      if (byWord && letters[i] === ' ') {
        word = null;
        holder.appendChild(document.createTextNode(' '));
        continue;
      }

      var span = document.createElement('span');
      span.className = 'letter';
      span.style.setProperty('--i', i);
      // A space has no glyph to move, but the span still has to hold its width
      // open, so it gets a non-breaking space and the word gap survives.
      span.textContent = letters[i] === ' ' ? ' ' : letters[i];
      if (byWord) {
        if (!word) {
          word = document.createElement('span');
          word.className = 'word';
          holder.appendChild(word);
        }
        word.appendChild(span);
      } else {
        holder.appendChild(span);
      }
    }

    heading.textContent = '';
    heading.appendChild(holder);
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-split-letters]').forEach(function (el) {
      split(el);
    });
  }

  window.yumbeiSplitLetters = function (element) {
    if (element) split(element, true);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
